import CreatorNameSection from '@/modules/contentCreator/CreatorNameSection';
import { ChangeEvent, FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Image,
  Input,
  Select,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/queryKeys';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trash2, X } from 'lucide-react';

import svgFiles from '@/assets/svg/svgFiles';
import InlineAddTextField from '@/components/atoms/InlineAddTextField';
import MyoSpinner from '@/components/atoms/LogoSpinner';
import roles from '@/constants/roles';
import useContentAuthorProfile from '@/hooks/content/useContentAuthorProfile';
import useContentCreatorApplicationMe from '@/hooks/contentcreator/useContentCreatorApplicationMe';
import useUSStates from '@/hooks/meta/useUSStates';
import useToast from '@/hooks/useToast';
import ContentFocusTile, { ContentFocusValue } from '@/modules/contentCreator/ContentFocusTile';
import { contentFocusOptions } from '@/modules/contentCreator/creatorSchema';
import {
  buildCreatorSocialLinks,
  creatorSocialMediaFields,
  getCreatorSocialUsername,
} from '@/modules/contentCreator/creatorSocialLinks';
import { creatorProfilePatch, creatorProfilePhotoPost, TUpdateCreatorProfile } from '@/services/api/requests/creators';
import { useAppStore } from '@/store/useAppStore';
import { useCreatorApplicationStore } from '@/store/useCreatorApplicationStore';
import { background, primary, text } from '@/theme/colors/palettes';

const headingStyles = {
  color: text.mediumBlue,
  fontSize: '18px',
  lineHeight: '24px',
  fontWeight: 700,
  textAlign: 'center' as const,
};

const CreatorProfileEdit = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useAppStore((store) => store.user);
  const hasMemberProfile = user?.roles.some((role) => role !== roles.contentCreator) ?? false;
  const realName = [user?.user_metadata.firstName, user?.user_metadata.lastName].filter(Boolean).join(' ');
  const previewData = useCreatorApplicationStore((state) => state.previewData);
  const setPreviewData = useCreatorApplicationStore((state) => state.setPreviewData);
  const {
    profile,
    isLoading: isCreatorProfileLoading,
    refetch: refetchCreatorProfile,
  } = useContentAuthorProfile(user?.user_id);
  const { data: creatorApplication } = useContentCreatorApplicationMe();
  const savedProfile = creatorApplication?.currentApplication?.profile;
  const [photoPreferenceReady, setPhotoPreferenceReady] = useState(false);
  const photoPreferenceInitialized = useRef(false);
  const { data: USStates } = useUSStates();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [keepMemberPhoto, setKeepMemberPhoto] = useState(false);
  const [creatorName, setCreatorName] = useState('');
  const [isCreatorNameTouched, setIsCreatorNameTouched] = useState(false);
  const [keepRealName, setKeepRealName] = useState(true);
  const creatorNameError = !keepRealName && !creatorName.trim() ? 'Creator name is required.' : undefined;

  const initialSocialHandles = useMemo(
    () =>
      creatorSocialMediaFields.map((_, index) =>
        getCreatorSocialUsername(previewData?.socialMediaHandles[index], index),
      ),
    [previewData],
  );
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [isProfileUpdating, setIsProfileUpdating] = useState(false);
  const [isProfilePhotoLoading, setIsProfilePhotoLoading] = useState(!previewData?.profilePhotoPreview);
  const [photoPreview, setPhotoPreview] = useState(previewData?.profilePhotoPreview || svgFiles.genericUser);
  const [aboutMe, setAboutMe] = useState(previewData?.aboutMe || profile?.headline || user?.user_metadata.bio || '');
  const [city, setCity] = useState(previewData?.city ?? user?.user_metadata.city ?? '');
  const [zipCode, setZipCode] = useState(previewData?.zipCode ?? user?.user_metadata.zip ?? '');
  const [state, setState] = useState(previewData?.state ?? user?.user_metadata.state ?? '');
  const [socialHandles, setSocialHandles] = useState(initialSocialHandles);
  const [qualificationColumns, setQualificationColumns] = useState<string[][]>(() => {
    const columns: string[][] = [[], []];
    (previewData?.qualifications ?? []).forEach((qualification, index) => columns[index % 2].push(qualification));
    return columns;
  });
  const [selectedContentFocus, setSelectedContentFocus] = useState<ContentFocusValue[]>(
    previewData?.contentFocus ?? (profile?.contentFocus as ContentFocusValue[] | undefined) ?? [],
  );

  const initialFormRef = useRef({
    photoPreview,
    keepMemberPhoto,
    creatorName,
    keepRealName,
    aboutMe,
    city,
    zipCode,
    state,
    socialHandles,
    qualifications: qualificationColumns.flat(),
    contentFocus: selectedContentFocus,
  });

  useEffect(() => {
    if (!savedProfile || photoPreferenceInitialized.current) return;
    photoPreferenceInitialized.current = true;
    const keepPhoto = savedProfile.keepPhotoSameAsMemberPhoto ?? true;
    setKeepMemberPhoto(keepPhoto);
    initialFormRef.current.keepMemberPhoto = keepPhoto;
    setCreatorName(savedProfile.creatorName ?? '');
    setKeepRealName(savedProfile.keepNameSameAsRealName ?? true);
    initialFormRef.current.creatorName = savedProfile.creatorName ?? '';
    initialFormRef.current.keepRealName = savedProfile.keepNameSameAsRealName ?? true;
    setPhotoPreferenceReady(true);
  }, [savedProfile]);

  const displayedPhoto =
    hasMemberProfile && keepMemberPhoto ? user?.user_metadata.profilePicture || svgFiles.genericUser : photoPreview;

  useEffect(() => {
    if (!previewData?.profilePhotoPreview && profile?.photoUrl) {
      setIsProfilePhotoLoading(true);
      setPhotoPreview(profile.photoUrl);
      initialFormRef.current.photoPreview = profile.photoUrl;
    }
    if (!previewData?.aboutMe && profile?.headline) {
      setAboutMe(profile.headline);
      initialFormRef.current.aboutMe = profile.headline;
    }
    if (!previewData?.contentFocus && profile?.contentFocus) {
      const profileContentFocus = profile.contentFocus as ContentFocusValue[];
      setSelectedContentFocus(profileContentFocus);
      initialFormRef.current.contentFocus = profileContentFocus;
    }
  }, [previewData, profile]);

  useEffect(() => {
    if (previewData?.socialMediaHandles.some((handle) => Boolean(handle?.trim()))) return;

    const socialLinks = creatorApplication?.currentApplication?.profile.socialLinks;
    if (!socialLinks) return;

    const handles = creatorSocialMediaFields.map(({ platform }, index) =>
      getCreatorSocialUsername(socialLinks.find((link) => link.platform === platform)?.url, index),
    );
    setSocialHandles(handles);
    initialFormRef.current.socialHandles = handles;
  }, [creatorApplication, previewData]);

  const isPhotoBusy = isPhotoUploading || isCreatorProfileLoading || isProfilePhotoLoading;

  const isPhotoSelectionDisabled =
    !photoPreferenceReady || isProfileUpdating || isPhotoBusy || (hasMemberProfile && keepMemberPhoto);

  const onPhotoChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (isPhotoSelectionDisabled) return;
    const file = event.target.files?.[0];
    if (!file || file.size > 3 * 1024 * 1024) return;

    setIsPhotoUploading(true);
    try {
      setIsProfilePhotoLoading(true);
      setPhotoPreview(URL.createObjectURL(file));

      // Uploading a separate photo must first stop resolving the member photo.
      if (savedProfile?.keepPhotoSameAsMemberPhoto) {
        await creatorProfilePatch({
          keepNameSameAsRealName: savedProfile.keepNameSameAsRealName,
          keepPhotoSameAsMemberPhoto: false,
        });
        initialFormRef.current.keepMemberPhoto = false;
        setKeepMemberPhoto(false);
        await queryClient.invalidateQueries({ queryKey: [queryKeys.contentCreatorApplications.me] });
      }
      await creatorProfilePhotoPost(file);
      await queryClient.invalidateQueries({ queryKey: [queryKeys.contentCreatorApplications.me] });

      refetchCreatorProfile();

      toast({
        status: 'success',
        title: 'Success',
        description: 'Creator profile photo updated successfully!',
        position: 'top-right',
      });
    } catch {
      if (photoInputRef.current) photoInputRef.current.value = '';
      setPhotoPreview(photoPreview);
      setIsProfilePhotoLoading(false);
      toast({
        status: 'error',
        title: 'Photo Upload Failed',
        description: 'Your profile photo could not be updated. Please try again.',
        position: 'top-right',
      });
    } finally {
      setIsPhotoUploading(false);
    }
  };

  const isDirty = useMemo(() => {
    const initial = initialFormRef.current;
    const sortValues = (values: string[]) => [...values].sort();

    return (
      creatorName !== initial.creatorName ||
      keepRealName !== initial.keepRealName ||
      keepMemberPhoto !== initial.keepMemberPhoto ||
      photoPreview !== initial.photoPreview ||
      aboutMe !== initial.aboutMe ||
      city !== initial.city ||
      zipCode !== initial.zipCode ||
      state !== initial.state ||
      JSON.stringify(socialHandles) !== JSON.stringify(initial.socialHandles) ||
      JSON.stringify(qualificationColumns.flat()) !== JSON.stringify(initial.qualifications) ||
      JSON.stringify(sortValues(selectedContentFocus)) !== JSON.stringify(sortValues(initial.contentFocus))
    );
  }, [
    aboutMe,
    city,
    keepMemberPhoto,
    creatorName,
    keepRealName,
    photoPreview,
    qualificationColumns,
    selectedContentFocus,
    socialHandles,
    state,
    zipCode,
  ]);

  const updateSocialHandle = (index: number, value: string) => {
    setSocialHandles((handles) => handles.map((handle, handleIndex) => (handleIndex === index ? value : handle)));
  };

  const addQualification = (columnIndex: number, newQualification: string) => {
    const value = newQualification.trim();
    if (!value) return;
    setQualificationColumns((columns) =>
      columns.map((column, index) => (index === columnIndex ? [...column, value] : column)),
    );
  };

  const toggleContentFocus = (value: ContentFocusValue) => {
    setSelectedContentFocus((current) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value],
    );
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!photoPreferenceReady || !savedProfile || isPhotoUploading || isProfileUpdating || creatorNameError) return;

    const supportedSocialPlatforms = new Set(['instagram', 'tiktok', 'youtube', 'linkedin', 'x']);

    const socialHandlesPayload = Object.fromEntries(
      buildCreatorSocialLinks(socialHandles)
        .filter(({ platform }) => supportedSocialPlatforms.has(platform))
        .map(({ platform, url }) => [platform, url]),
    ) as NonNullable<TUpdateCreatorProfile['socialHandles']>;

    const location = {
      ...(city.trim() && { city: city.trim() }),
      ...(state && { state: state as NonNullable<TUpdateCreatorProfile['location']>['state'] }),
      ...(zipCode.trim() && { zip: zipCode.trim() }),
    };

    const profileUpdate: TUpdateCreatorProfile = {
      creatorName: creatorName.trim() || undefined,
      keepNameSameAsRealName: keepRealName,
      keepPhotoSameAsMemberPhoto: keepMemberPhoto,
      aboutMe: aboutMe.trim(),
      location,
      socialHandles: socialHandlesPayload,
      qualifications: qualificationColumns.flat().map((qualification) => qualification.trim()),
      contentFocus: selectedContentFocus,
    };

    setIsProfileUpdating(true);
    try {
      const updatedProfile = await creatorProfilePatch(profileUpdate);
      setPreviewData({
        profilePhoto: null,
        creatorName: updatedProfile.creatorName ?? creatorName.trim(),
        keepNameSameAsRealName: keepRealName,
        keepPhotoSameAsMemberPhoto: keepMemberPhoto,
        existingProfilePhotoUrl: updatedProfile.photoUrl ?? displayedPhoto,
        profilePhotoPreview: updatedProfile.photoUrl ?? displayedPhoto,
        aboutMe,
        city,
        zipCode,
        state,
        socialMediaHandles: socialHandles,
        qualifications: qualificationColumns.flat(),
        contentFocus: selectedContentFocus,
      });
      await refetchCreatorProfile();

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [queryKeys.contentCreatorApplications.me] }),
        ...Object.values(queryKeys.content).map((key) => queryClient.invalidateQueries({ queryKey: [key] })),
      ]);

      toast({
        status: 'success',
        title: 'Success',
        description: 'Creator profile updated successfully!',
        position: 'top-right',
      });
      navigate({ to: '/creator/profile' });
    } catch {
      toast({
        status: 'error',
        title: 'Profile Update Failed',
        description: 'Your creator profile could not be updated. Please try again.',
        position: 'top-right',
      });
    } finally {
      setIsProfileUpdating(false);
    }
  };

  return (
    <Box width={'100%'} background={background.lightBlue} paddingBottom={'80px'}>
      <VStack
        as={'form'}
        onSubmit={onSubmit}
        width={'610px'}
        maxWidth={'calc(100vw - 32px)'}
        marginX={'auto'}
        spacing={{ base: '40px', md: '52px' }}
        alignItems={'stretch'}
      >
        <Text
          fontFamily={'Lora'}
          color={text.darkBlue}
          fontSize={'26px'}
          fontStyle={'italic'}
          fontWeight={500}
          lineHeight={'32px'}
          textAlign={'center'}
        >
          Edit Creator Profile
        </Text>

        <Flex
          minHeight={'160px'}
          background={'white'}
          borderRadius={'16px'}
          boxShadow={'0 18px 45px rgba(0, 65, 121, 0.12)'}
          padding={{ base: '28px', md: '38px 46px' }}
          alignItems={'center'}
          justifyContent={'space-between'}
          direction={{ base: 'column', md: 'row' }}
          gap={'28px'}
        >
          <VStack alignItems={{ base: 'center', md: 'flex-start' }} spacing={'5px'}>
            <Text color={text.darkBlue} fontSize={'16px'} fontWeight={600}>
              Creator Profile Photo
            </Text>
            <Text color={text.mediumBlue} fontSize={'14px'}>
              Not smaller than 100x100px and not larger than 3MB.
            </Text>

            <HStack spacing={'12px'} paddingTop={'8px'}>
              <Button
                type={'button'}
                width={'140px'}
                color={'white'}
                backgroundColor={primary[500]}
                fontWeight={700}
                isDisabled={isPhotoSelectionDisabled}
                isLoading={isPhotoUploading}
                onClick={() => photoInputRef.current?.click()}
                _hover={{ background: 'primary.600' }}
                boxShadow={'0 4px 14px rgba(14, 0, 98, 0.2)'}
              >
                Upload
              </Button>
            </HStack>
          </VStack>
          <Box
            height={'120px'}
            width={'120px'}
            flexShrink={0}
            borderRadius={'full'}
            overflow={'hidden'}
            position={'relative'}
            border={'5px solid white'}
            boxShadow={'0 8px 24px rgba(14, 0, 98, 0.2)'}
          >
            <Image
              src={displayedPhoto}
              alt={'Creator profile'}
              width={'100%'}
              height={'100%'}
              objectFit={'cover'}
              opacity={isPhotoBusy ? 0.45 : 1}
              onLoad={() => setIsProfilePhotoLoading(false)}
              onError={() => setIsProfilePhotoLoading(false)}
            />
            {isPhotoBusy && (
              <Flex position={'absolute'} inset={0} alignItems={'center'} justifyContent={'center'}>
                <MyoSpinner size={'48px'} />
              </Flex>
            )}
          </Box>
          <Input
            ref={photoInputRef}
            type={'file'}
            accept={'image/*'}
            display={'none'}
            onChange={onPhotoChange}
            isDisabled={isPhotoSelectionDisabled}
          />
        </Flex>

        {hasMemberProfile && (
          <VStack alignItems={'stretch'} spacing={'10px'} paddingX={'22px'} marginTop={'-24px'}>
            <Text
              id={'member-photo-description'}
              color={text.mediumGray}
              fontSize={'16px'}
              lineHeight={'20px'}
              fontWeight={500}
            >
              You can upload new photo or use one from your member profile, if so they will be synced in future.
            </Text>
            <Checkbox
              isChecked={keepMemberPhoto}
              onChange={(event) => setKeepMemberPhoto(event.target.checked)}
              isDisabled={!photoPreferenceReady || isPhotoUploading || isProfileUpdating}
              aria-describedby={'member-photo-description'}
              size={'lg'}
              spacing={'12px'}
              colorScheme={'primary'}
            >
              <Text fontFamily={'Urbanist'} color={text.mediumGray} fontSize={'16px'} fontWeight={500}>
                Keep same as my member photo
              </Text>
            </Checkbox>
          </VStack>
        )}

        <CreatorNameSection
          textColor={text.mediumGray}
          realName={realName}
          creatorName={creatorName}
          keepNameSameAsRealName={keepRealName}
          onCreatorNameChange={setCreatorName}
          onKeepNameSameAsRealNameChange={setKeepRealName}
          onBlur={() => setIsCreatorNameTouched(true)}
          error={isCreatorNameTouched ? creatorNameError : undefined}
          isDisabled={!photoPreferenceReady || isProfileUpdating}
        />

        <VStack spacing={'22px'} alignItems={'stretch'}>
          <Text {...headingStyles}>About Me</Text>
          <Textarea
            value={aboutMe}
            onChange={(event) => setAboutMe(event.target.value)}
            minHeight={'190px'}
            resize={'vertical'}
            background={'white'}
            border={'1px solid'}
            borderColor={background.darkGray}
            borderRadius={'10px'}
            padding={'25px 28px'}
            color={text.mediumBlue}
            fontSize={'14px'}
            lineHeight={'18px'}
            fontWeight={600}
            boxShadow={'0 2px 5px rgba(0, 65, 121, 0.06)'}
            placeholder={'Tell people about yourself and your work.'}
          />
        </VStack>

        <VStack spacing={'22px'} alignItems={'stretch'}>
          <Text {...headingStyles}>Location</Text>
          <Text color={text.mediumBlue} fontSize={'14px'} fontWeight={500}>
            Changes to your location will also update your member profile.
          </Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={'20px'}>
            <HStack spacing={'10px'}>
              <Input
                value={city}
                onChange={(event) => setCity(event.target.value)}
                placeholder={'City'}
                className={'custom-input'}
                color={'text.darkBlue'}
                _placeholder={{ color: 'text.mediumGray', fontWeight: 600, fontSize: '12px' }}
              />
              <Input
                value={zipCode}
                onChange={(event) => setZipCode(event.target.value)}
                placeholder={'ZIP Code'}
                className={'custom-input'}
                color={'text.darkBlue'}
                minWidth={'70px'}
                inputMode={'numeric'}
                _placeholder={{ color: 'text.mediumGray', fontWeight: 600, fontSize: '13px' }}
              />
            </HStack>
            <Select
              value={state}
              onChange={(event) => setState(event.target.value)}
              className={'custom-input custom-select-padding'}
              color={state ? 'text.darkBlue' : 'text.mediumGray'}
              fontSize={state ? undefined : '14px'}
            >
              <option value="" disabled>
                State
              </option>
              {(USStates ?? []).map((item) => (
                <option key={item.code} value={item.code}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Grid>
        </VStack>

        <VStack spacing={'22px'} alignItems={'stretch'}>
          <Text {...headingStyles}>Social Media Handles</Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={'20px 28px'}>
            {creatorSocialMediaFields.map(({ platform, validation }, index) => {
              const value = socialHandles[index];
              const label = platform === 'x' ? 'X' : `${platform.charAt(0).toUpperCase()}${platform.slice(1)}`;
              return (
                <Box key={platform} position={'relative'}>
                  <HStack
                    className={'custom-input'}
                    width={'100%'}
                    height={'54px'}
                    minHeight={'54px'}
                    maxHeight={'54px'}
                    spacing={'8px'}
                    paddingRight={value ? '40px' : '16px'}
                  >
                    <Text flexShrink={0} fontSize={'14px'} fontWeight={800} whiteSpace={'nowrap'}>
                      {validation}
                    </Text>
                    <Input
                      value={value}
                      onChange={(event) =>
                        updateSocialHandle(index, getCreatorSocialUsername(event.target.value, index))
                      }
                      placeholder={`${label} username`}
                      variant={'unstyled'}
                      minWidth={0}
                      padding={0}
                      color={'text.darkBlue'}
                      _placeholder={{ color: 'text.mediumGray', fontWeight: 600, fontSize: '13px', opacity: 0.65 }}
                    />
                  </HStack>
                  {value && (
                    <Button
                      position={'absolute'}
                      right={'6px'}
                      top={'50%'}
                      transform={'translateY(-50%)'}
                      height={'32px'}
                      minWidth={'20px'}
                      width={'30px'}
                      padding={0}
                      variant={'ghost'}
                      color={'extra.red'}
                      onClick={() => updateSocialHandle(index, '')}
                      aria-label={`Clear ${label}`}
                    >
                      <X size={18} strokeWidth={3} />
                    </Button>
                  )}
                </Box>
              );
            })}
          </Grid>
        </VStack>

        <VStack spacing={'22px'} alignItems={'stretch'}>
          <VStack spacing={'2px'}>
            <Text {...headingStyles}>Qualifications or Certifications (optional)</Text>
            <Text color={text.mediumGray} fontSize={'16px'} fontWeight={600}>
              Example: certified peer support.
            </Text>
          </VStack>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={'20px 28px'}>
            {qualificationColumns.map((column, columnIndex) => (
              <VStack key={columnIndex} spacing={'16px'} alignItems={'stretch'}>
                {column.map((qualification, qualificationIndex) => (
                  <HStack
                    key={`${qualification}-${qualificationIndex}`}
                    minHeight={'54px'}
                    background={'#FFFCF7'}
                    border={'1px solid'}
                    borderColor={'secondary.300'}
                    borderRadius={'8px'}
                    padding={'0 12px 0 18px'}
                    justifyContent={'space-between'}
                  >
                    <Text color={text.mediumBlue} fontSize={'14px'} fontWeight={600}>
                      {qualification}
                    </Text>
                    <Button
                      variant={'ghost'}
                      color={'extra.red'}
                      minWidth={'36px'}
                      onClick={() =>
                        setQualificationColumns((columns) =>
                          columns.map((items, index) =>
                            index === columnIndex
                              ? items.filter((_, itemIndex) => itemIndex !== qualificationIndex)
                              : items,
                          ),
                        )
                      }
                      aria-label={`Delete ${qualification}`}
                    >
                      <Trash2 size={19} fill={'currentColor'} />
                    </Button>
                  </HStack>
                ))}
                <InlineAddTextField
                  placeholder={'Add Qualification'}
                  minChars={2}
                  maxChars={120}
                  minHeight={'54px'}
                  onSubmit={(value) => addQualification(columnIndex, value)}
                />
              </VStack>
            ))}
          </Grid>
        </VStack>

        <VStack spacing={'22px'} alignItems={'stretch'}>
          <Text {...headingStyles}>Content Focus</Text>
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={'14px 20px'}>
            {contentFocusOptions.map((option) => (
              <ContentFocusTile
                key={option.value}
                label={option.label}
                value={option.value}
                isSelected={selectedContentFocus.includes(option.value)}
                onToggle={toggleContentFocus}
              />
            ))}
          </Grid>
        </VStack>

        <Button
          type={'submit'}
          width={'100%'}
          height={'54px'}
          background={'secondary.500'}
          color={'white'}
          fontSize={'16px'}
          fontWeight={700}
          isDisabled={
            !photoPreferenceReady || !isDirty || isPhotoUploading || isProfileUpdating || Boolean(creatorNameError)
          }
          isLoading={isPhotoUploading || isProfileUpdating}
          _hover={{ background: 'secondary.600' }}
          _disabled={{ background: 'secondary.300', cursor: 'not-allowed', opacity: 0.55 }}
        >
          Save Updates
        </Button>
      </VStack>
    </Box>
  );
};

export const Route = createFileRoute('/_authenticated/_creator/creator/profile/edit/')({
  staticData: { title: 'Edit Creator Profile' },
  component: CreatorProfileEdit,
});
